import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { paymentAPI, userAPI } from '../services/api';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import { useUser } from '../contexts/UserContext';
import { formatPrice } from '@shared/utils';

interface LocationState {
  packageName: string;
  amount: number;
  giftCardUsed: boolean;
  packageType?: string;
  packageId?: string;
  customerEmail?: string;
  customerPhone?: string;
  coupon?: string;
}

const PurchaseConfirmationPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const [searchParams] = useSearchParams();
  const isSubscription = state?.packageType === 'subscription';
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);
  const contractCreatedRef = useRef(false);

  useDocumentTitle(t.confirmation.title);

  const hasTrackedPurchase = useRef(false);
  const { user } = useUser();
  const [customerPhone, setCustomerPhone] = useState<string>('');

  useEffect(() => {
    const paymentIntentId = searchParams.get('payment_intent');
    const packageId = searchParams.get('packageId');

    if (paymentIntentId && packageId && paymentIntentId !== 'gift_card_payment' && !contractCreatedRef.current) {
      contractCreatedRef.current = true;
      const createContract = async () => {
        setIsCreatingContract(true);
        setContractError(null);
        try {
          await paymentAPI.createContractAfterPayment({ paymentIntentId, packageId });
        } catch (error) {
          console.error('Failed to create contract:', error);
          setContractError(t.confirmation.contractError || 'There was an issue activating your package. Please contact support.');
          contractCreatedRef.current = false;
        } finally {
          setIsCreatingContract(false);
        }
      };
      createContract();
    }
  }, [searchParams, t.confirmation.contractError]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await userAPI.getProfile();
        if (response.data.phoneNumber) {
          setCustomerPhone(response.data.phoneNumber);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const paymentIntentId = searchParams.get('payment_intent');
    const packageId = searchParams.get('packageId') || state?.packageId;
    const { amount, packageName, packageType, customerEmail: stateEmail, customerPhone: statePhone, coupon } = state || {};

    if (!hasTrackedPurchase.current && (paymentIntentId || packageId) && amount !== undefined) {
      hasTrackedPurchase.current = true;
      clearEcommerce();
      
      const email = stateEmail || user?.email || '';
      const phone = statePhone || customerPhone || '';

      pushToDataLayer('purchase', {
        content: { category: packageType === 'subscription' ? 'subscription' : 'package' },
        ecommerce: {
          transaction_id: paymentIntentId || packageId,
          value: amount,
          currency: 'EUR',
          ...(coupon && { coupon }),
          items: [{
            item_id: packageId,
            item_name: packageName,
            item_category: packageType === 'subscription' ? 'Subscription' : 'Package',
            value: amount
          }]
        },
        customer: {
          email,
          phone
        }
      });
    }
  }, [searchParams, state, user, customerPhone]);

  if (!state) {
    return (
      <div className="container mx-auto p-4 md:p-8 max-w-4xl">
        <div className="text-center py-10">
          <p className="text-gray-600 mb-4">{t.confirmation.noData}</p>
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:underline"
          >
            {t.common.goHome}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t.confirmation.successTitle}
          </h1>
          <p className="text-lg text-gray-600">
            {t.confirmation.successMessage}
          </p>
        </div>

        <div className="bg-gray-50 rounded-md p-6 mb-6">
          <h2 className="font-semibold text-lg mb-4">{t.confirmation.orderDetails}</h2>
          <div className="space-y-2 text-left max-w-sm mx-auto">
            <div className="flex justify-between">
              <span className="text-gray-600">{t.confirmation[isSubscription ? 'subscription' : 'package']}:</span>
              <span className="font-medium">{state.packageName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t.confirmation.amountPaid}:</span>
              <span className="font-medium">{formatPrice(state.amount)}</span>
            </div>
            {state.giftCardUsed && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t.confirmation.paymentMethod}:</span>
                <span className="font-medium">
                  {state.amount === 0
                    ? t.confirmation.giftCardOnly
                    : t.confirmation.cardAndGiftCard}
                </span>
              </div>
            )}
          </div>
        </div>

        {isCreatingContract && (
          <div className="text-center py-4 text-gray-600">
            <p>{t.confirmation.creatingContract || 'Finalizing your purchase, please wait...'}</p>
          </div>
        )}
        {contractError && (
          <div className="text-center py-4 text-red-600 bg-red-50 p-3 rounded-md">
            <p>{contractError}</p>
          </div>
        )}

        <div className="space-y-3">
          <h3 className="font-semibold text-lg mb-2">{t.confirmation.whatNext}</h3>
          <p className="text-gray-600 mb-4">
            {t.confirmation.confirmationEmail}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/book')}
              className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
            >
              {t.confirmation.bookSession}
            </button>
            <button
              onClick={() => navigate('/account')}
              className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium"
            >
              {t.confirmation.goToAccount}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseConfirmationPage;